import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Hammer, Loader2, X } from "lucide-react";
import { updateInitiativeItem } from "../api/graph";
import { Avatar } from "./Avatar";
import { daysSince, relativeTime } from "../utils/time";
import type { InitiativeItem } from "../types/navigator";
import { logActivity } from "../api/activityLog";

interface Props {
  items: InitiativeItem[];
  onClose: () => void;
  onSelect: (item: InitiativeItem) => void;
  getToken: () => Promise<string>;
  /** Called after a successful stage move so the parent can refetch and the
   *  lanes reflect the new status. */
  onMoved: () => void;
}

// The three active-build stages, in pipeline order — separate from
// statusCategory's coarser "inProgress" bucket since this view needs each
// stage broken out into its own lane, not lumped together. Moving a card
// only ever slides it one step within this list (Design ⇄ Development ⇄
// Testing) — it never jumps out to Backlog or Live, so a quick drag/click
// here can't accidentally skip an approval stage.
const STAGES = ["Design", "Development", "Testing"] as const;
type Stage = (typeof STAGES)[number];

/** Flag anything not touched in a while — the whole point of this view is
 *  surfacing citizen-dev work that may have stalled, not just what's
 *  technically "in progress" on paper. */
const STALE_DAYS = 30;

function stageOf(item: InitiativeItem): Stage | null {
  const s = (item.status || "").trim().toLowerCase();
  return STAGES.find((stage) => stage.toLowerCase() === s) ?? null;
}

export function InProgressModal({ items, onClose, onSelect, getToken, onMoved }: Props) {
  const [moving, setMoving] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void logActivity("Feature", "InProgressOpened", "Opened the In progress board");
  }, []);

  const byStage = new Map<Stage, InitiativeItem[]>(STAGES.map((s) => [s, []]));
  for (const item of items) {
    const stage = stageOf(item);
    if (stage) byStage.get(stage)!.push(item);
  }
  for (const list of byStage.values()) {
    list.sort((a, b) => (daysSince(b.modified) ?? 0) - (daysSince(a.modified) ?? 0));
  }
  const total = items.reduce((sum, i) => sum + (stageOf(i) ? 1 : 0), 0);

  const move = async (item: InitiativeItem, direction: -1 | 1) => {
    const stage = stageOf(item);
    if (!stage) return;
    const nextStage = STAGES[STAGES.indexOf(stage) + direction];
    if (!nextStage) return;
    setError(null);
    setMoving((m) => ({ ...m, [item.id]: true }));
    try {
      const token = await getToken();
      await updateInitiativeItem(token, item.id, { Status: nextStage });
      onMoved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setMoving((m) => ({ ...m, [item.id]: false }));
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal inprogress-modal" role="dialog" aria-modal="true">
        <div className="modal-accent" aria-hidden />
        <div className="modal-head">
          <div className="modal-head-icon icon-initiative" aria-hidden>
            <Hammer size={22} />
          </div>
          <div className="modal-head-text">
            <span className="modal-kind">Citizen development</span>
            <h2 className="modal-title">In progress</h2>
            <p className="add-item-subtitle">
              {total} initiative{total === 1 ? "" : "s"} actively being built —
              use the arrows to move a card between stages.
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="inprogress-body">
          {error ? (
            <div className="add-item-error inprogress-error" role="alert">
              <strong>Couldn't move that.</strong> {error}
            </div>
          ) : null}

          {total === 0 ? (
            <div className="backlog-empty">Nothing in Design, Development, or Testing right now.</div>
          ) : (
            <div className="inprogress-lanes">
              {STAGES.map((stage, stageIdx) => {
                const stageItems = byStage.get(stage)!;
                return (
                  <div key={stage} className="inprogress-lane">
                    <div className="inprogress-lane-head">
                      <span>{stage}</span>
                      <span className="inprogress-lane-count">{stageItems.length}</span>
                    </div>
                    <div className="inprogress-lane-items">
                      {stageItems.length === 0 ? (
                        <div className="inprogress-lane-empty">Nothing here</div>
                      ) : (
                        stageItems.map((item) => {
                          const stale = (daysSince(item.modified) ?? 0) > STALE_DAYS;
                          const isMoving = !!moving[item.id];
                          return (
                            <div
                              key={item.id}
                              className={`inprogress-card${stale ? " is-stale" : ""}`}
                            >
                              <button
                                type="button"
                                className="inprogress-card-main"
                                onClick={() => onSelect(item)}
                              >
                                <span className="inprogress-card-title">
                                  {item.title || "Untitled initiative"}
                                </span>
                                <span className="inprogress-card-meta">
                                  <Avatar name={item.submittedBy || "?"} size={16} />
                                  <span>{item.developmentTeam || item.submittedBy || "Unassigned"}</span>
                                </span>
                                <span className={`inprogress-card-updated${stale ? " is-stale" : ""}`}>
                                  {stale ? "Stale — " : "Updated "}
                                  {relativeTime(item.modified) || "unknown"}
                                </span>
                              </button>
                              <div className="inprogress-card-actions">
                                <button
                                  type="button"
                                  className="inprogress-move-btn"
                                  disabled={stageIdx === 0 || isMoving}
                                  onClick={() => move(item, -1)}
                                  aria-label={`Move back to ${STAGES[stageIdx - 1] ?? ""}`}
                                  title={stageIdx > 0 ? `Move to ${STAGES[stageIdx - 1]}` : undefined}
                                >
                                  <ChevronLeft size={13} />
                                </button>
                                {isMoving ? (
                                  <Loader2 size={13} className="add-item-spin inprogress-move-spinner" />
                                ) : null}
                                <button
                                  type="button"
                                  className="inprogress-move-btn"
                                  disabled={stageIdx === STAGES.length - 1 || isMoving}
                                  onClick={() => move(item, 1)}
                                  aria-label={`Move forward to ${STAGES[stageIdx + 1] ?? ""}`}
                                  title={
                                    stageIdx < STAGES.length - 1 ? `Move to ${STAGES[stageIdx + 1]}` : undefined
                                  }
                                >
                                  <ChevronRight size={13} />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
